import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';
import { CandidateApplySource, CandidateApplyTokenVerification, M2mService } from '../../core/services/m2m/m2m.service';

/** Source the page pre-selects when the gateway allows it — the reason this page exists. */
const PREFERRED_SOURCE = 'kernel-app';

@Component({
  selector: 'app-m2m-tokens',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatCard,
    MatCardContent,
    MatCardHeader,
    MatCardTitle,
    MatFormField,
    MatHint,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
  ],
  templateUrl: './m2m-tokens.component.html',
  styleUrl: './m2m-tokens.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class M2mTokensComponent {
  private readonly m2mService = inject(M2mService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);

  public readonly sourceControl = new FormControl<string | null>(null);
  public readonly tokenControl = new FormControl<string>('');

  public readonly sources = signal<CandidateApplySource[]>([]);
  public readonly sourcesLoading = signal(true);
  public readonly generating = signal(false);
  public readonly generatedToken = signal<string | null>(null);

  public readonly verifying = signal(false);
  public readonly verification = signal<CandidateApplyTokenVerification | null>(null);

  constructor() {
    this.m2mService
      .getCandidateApplySources()
      .pipe(
        finalize(() => this.sourcesLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ sources }) => {
        this.sources.set(sources);

        const preferred = sources.find(({ source }) => source === PREFERRED_SOURCE);
        this.sourceControl.setValue(preferred?.source ?? sources[0]?.source ?? null);
      });
  }

  public generateToken(): void {
    const source = this.sourceControl.value;

    if (!source) {
      return;
    }

    this.generating.set(true);
    this.generatedToken.set(null);

    this.m2mService
      .generateCandidateApplyToken(source)
      .pipe(
        finalize(() => this.generating.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((token) => {
        this.generatedToken.set(token.token);
        this.snackBar.open(`Токен для «${token.source}» згенеровано.`, 'OK', { duration: 3000 });
      });
  }

  public verifyToken(): void {
    const token = this.tokenControl.value?.trim();

    if (!token) {
      return;
    }

    this.verifying.set(true);
    this.verification.set(null);

    this.m2mService
      .verifyCandidateApplyToken(token)
      .pipe(
        finalize(() => this.verifying.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      // An invalid token is a 200 with `valid: false` — the answer an admin came
      // here for, not an error, so it is rendered rather than thrown at a snackbar.
      .subscribe((verification) => this.verification.set(verification));
  }
}
